import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { useEffect, useState, useRef } from 'react';
import { CancelIcon, CartIcon, animationDurationInMS } from '@chegg-ui/theme';
import { sessionLogger } from '@chegg-ui/utils';
import { clickBaseEvent } from '@chegg-me-components/utils';
import { defaultCentralNavLinks } from '../../externals';
import CSPOnboarding from '../../internals/CSPOnboarding';
import { SessionSpecificItems, CartLink, LegacyHeaderSearchContainer, CloseButton, DesktopCoreNavGroup } from '../../styles';
import { ExplicitlyHideSearchBar, NavType } from '../../types';
import HeaderLogoLink from '../HeaderLogoLink';
import getSecondaryNavLinks from '../getSecondaryNavLinks';
import { useVisibleLinks } from '../useVisibleEligibility';
import { rioClient } from './../rio';
import DesktopCenterNav from './DesktopCenterNav';
import DesktopHamburgerMenu from './DesktopHamburgerMenu';
import DesktopUserMenu from './DesktopUserMenu';
import UserInfoLoader from './UserInfoLoader';

var DesktopHeader = function DesktopHeader(_ref) {
  var navType = _ref.navType,
      envUrl = _ref.envUrl,
      pageTrackId = _ref.pageTrackId,
      activeSubLink = _ref.activeSubLink,
      cartCount = _ref.cartCount,
      user = _ref.user,
      customCenterNavConfig = _ref.customCenterNavConfig,
      mainContentId = _ref.mainContentId,
      onClickSignIn = _ref.onClickSignIn,
      onClickSignUp = _ref.onClickSignUp,
      onClickSignOut = _ref.onClickSignOut,
      userDataLoading = _ref.userDataLoading,
      CSPOnboardingEnabled = _ref.CSPOnboardingEnabled,
      CSPOnboardingOptions = _ref.CSPOnboardingOptions,
      children = _ref.children;
  var isLegacyHeaderSearch = children !== ExplicitlyHideSearchBar && Object.keys(children.props).includes('serverRenderAsMobile');

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      searchActive = _useState2[0],
      setSearchActive = _useState2[1];

  var adaptiveHeaderSearchRef = useRef(null);

  var setSearchInactive = function setSearchInactive() {
    return setSearchActive(false);
  };

  useEffect(function () {
    var mainContentElement = document.getElementById(mainContentId);

    if (searchActive && mainContentElement) {
      mainContentElement.addEventListener('click', setSearchInactive);
    }

    return function () {
      if (mainContentElement) {
        mainContentElement.removeEventListener('click', setSearchInactive);
      }
    };
  }, [searchActive]);

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      searchActivating = _useState4[0],
      setSearchActivating = _useState4[1];

  var _useVisibleLinks = useVisibleLinks(navType, customCenterNavConfig ? customCenterNavConfig : defaultCentralNavLinks[navType], user),
      links = _useVisibleLinks.links,
      eligibility = _useVisibleLinks.eligibility;
  /**
   * Rio log for cart count
   */


  var cartClickLog = function cartClickLog(count) {
    rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
      data: {
        interaction: {
          type: 'click',
          element: {
            name: 'cart',
            type: 'icon',
            region_name: 'nav header',
            value: count
          }
        }
      }
    }));
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DesktopCoreNavGroup, null, !searchActive && /*#__PURE__*/React.createElement(DesktopHamburgerMenu, {
    envUrl: envUrl,
    pageTrackId: pageTrackId,
    navType: navType,
    eligibility: eligibility
  }), /*#__PURE__*/React.createElement(HeaderLogoLink, {
    envUrl: envUrl,
    navType: navType,
    onClick: function onClick() {
      /**
       * Rio logging for click on chegg logo
       * using force here as there will be redirection after click
       */
      rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
        data: {
          interaction: {
            type: 'select',
            element: {
              name: 'nav header shortcut',
              type: 'link',
              region_name: 'nav header',
              value: navType === NavType.home ? 'Chegg' : "Chegg ".concat(navType)
            }
          }
        }
      }));
      sessionLogger.event(envUrl, pageTrackId, {
        navigation_click: "".concat(navType, "-logo")
      });
    }
  }), !searchActive && !searchActivating && /*#__PURE__*/React.createElement(DesktopCenterNav, {
    navLinks: links,
    navType: navType,
    envUrl: envUrl,
    pageTrackId: pageTrackId,
    activeSubLink: activeSubLink
  })), isLegacyHeaderSearch && !userDataLoading && // intentionally hiding search while fetching user data, to remove layout shift
  true && /*#__PURE__*/React.createElement(LegacyHeaderSearchContainer, {
    searchActive: searchActive,
    searchActivating: searchActivating,
    ref: adaptiveHeaderSearchRef
  }, /*#__PURE__*/React.cloneElement(children, {
    searchActive: searchActive,
    pageLocation: navType,
    onClickSearchBox: function onClickSearchBox() {
      /**
       * Rio logging for search box
       */
      rioClient.sendEvent(_objectSpread(_objectSpread({}, clickBaseEvent), {}, {
        data: {
          interaction: {
            type: 'search',
            element: {
              name: 'website',
              type: 'button',
              region_name: "".concat(navType, " nav header"),
              value: ''
            }
          }
        }
      })); // intentional no-op if search is closed, so nothing to really test

      /* istanbul ignore else */

      if (!searchActive) {
        setSearchActivating(true);

        var activate = function activate() {
          setSearchActive(true);
          setSearchActivating(false);
        };

        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          activate();
        } else {
          setTimeout(activate, animationDurationInMS.slower);
        }
      }
    }
  })), searchActive ? /*#__PURE__*/React.createElement(CloseButton, {
    size: "large",
    screenReaderOnlyText: "cancel search",
    "data-test": "header-close-search",
    onClick: function onClick() {
      setSearchInactive(); // search box only ever has one button within it

      var searchBoxButton = adaptiveHeaderSearchRef.current && adaptiveHeaderSearchRef.current.querySelector('button');
      /* istanbul ignore else */

      if (searchBoxButton) {
        searchBoxButton.focus();
      }
    }
  }, /*#__PURE__*/React.createElement(CancelIcon, {
    size: "large"
  })) : userDataLoading && // when this is true, it'll hide CSPOnboarding, CartInfo and UserMenu
  true ? /*#__PURE__*/React.createElement(UserInfoLoader, null) : /*#__PURE__*/React.createElement(SessionSpecificItems, null, CSPOnboardingEnabled && /*#__PURE__*/React.createElement(CSPOnboarding, {
    CSPOnboardingEnabled: CSPOnboardingEnabled,
    CSPOnboardingOptions: CSPOnboardingOptions
  }), cartCount && cartCount > 0 ? /*#__PURE__*/React.createElement(CartLink, {
    href: "".concat(envUrl, "shoppingcart"),
    onClick: function onClick() {
      return cartClickLog("".concat(cartCount));
    },
    "data-test": "header-cart"
  }, /*#__PURE__*/React.createElement(CartIcon, {
    size: "medium",
    numItems: cartCount
  })) : null, /*#__PURE__*/React.createElement(DesktopUserMenu, {
    navType: navType,
    user: user,
    envUrl: envUrl,
    pageTrackId: pageTrackId,
    onClickSignIn: onClickSignIn,
    onClickSignUp: onClickSignUp // This is only used in case of `navType = uversity`
    // TODO infra: memoize links? https://chegg.atlassian.net/browse/CUI-368
    ,
    userLinks: getSecondaryNavLinks({
      navType: navType,
      user: user,
      onClickSignUp: onClickSignUp,
      onClickSignOut: onClickSignOut,
      limitNavigation: navType === NavType.uversity // limiting navigation entries in case of Navtype = uversity

    })
  })));
};

DesktopHeader.displayName = 'DesktopHeader';
export default DesktopHeader;